import React, { useState, useEffect } from "react";
import axios from "axios";
import moment from "moment";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Table,
  Modal,
  Card,
  Badge,
  Tab,
  Tabs,
  Alert
} from "react-bootstrap";
import { io } from "socket.io-client";
import {
  FaFilter,
  FaTrash,
  FaEdit,
  FaHistory,
  FaDownload,
  FaCalendarAlt,
  FaUserCheck,
  FaClock,
  FaMapMarkerAlt,
  FaExclamationCircle,
  FaPlus,
  FaUser
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const UpdateAttendance = () => {
  // States for form fields and data
  const [employees, setEmployees] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [approvedBy, setApprovedBy] = useState("");
  const [reason, setReason] = useState("");
  const [location, setLocation] = useState("");
  const [clockIn, setClockIn] = useState("");
  const [clockOut, setClockOut] = useState("");
  const [updateClockIn, setUpdateClockIn] = useState(false);
  const [updateClockOut, setUpdateClockOut] = useState(false);
  const [manualSelection, setManualSelection] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [updateApprovedBy, setUpdateApprovedBy] = useState(false);
  const [updateReason, setUpdateReason] = useState(false);
  const [updateLocation, setUpdateLocation] = useState(false);
  
  // Add Log Modal states
  const [showAddLogModal, setShowAddLogModal] = useState(false);
  const [newLog, setNewLog] = useState({
    employee: "",
    date: "",
    clockIn: "",
    clockOut: "",
    reason: "",
    location: "",
    approvedBy: ""
  });
  const [validationErrors, setValidationErrors] = useState({});

  // Update confirmation modal
  const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);
  
  // Logs management states
  const [attendanceLogs, setAttendanceLogs] = useState([]);
  const [filterMonth, setFilterMonth] = useState("");
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [logsFilter, setLogsFilter] = useState({
    dateFrom: "",
    dateTo: "",
    changedBy: "",
    employee: "",
    fieldName: ""
  });
  
  // Filter states
  const [filterType, setFilterType] = useState("date");
  const [filterDate, setFilterDate] = useState("");
  const [filterApprovedBy, setFilterApprovedBy] = useState("");
  const [filterEmployee, setFilterEmployee] = useState("");
  const [showFilterBar, setShowFilterBar] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(20);
  const [activeTab, setActiveTab] = useState("records");
  
  // Loading states
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [submittingNewRecord, setSubmittingNewRecord] = useState(false);

  // Fetch attendance records
  const fetchAttendanceRecords = async () => {
    setLoadingRecords(true);
    try {
      const response = await axios.get(`${API_URL}/api/attendance?t=${Date.now()}`);
      const sorted = response.data.sort((a, b) => new Date(b.date) - new Date(a.date));
      setAttendanceRecords(sorted);
    } catch (error) {
      console.error("Error fetching attendance records:", error);
      toast.error("Failed to load attendance records.");
    } finally {
      setLoadingRecords(false);
    }
  };

  // Fetch employees
  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`);
      setEmployees(
        response.data
          .map((u) => ({ emp_name: u.emp_name || u.Name || u.username }))
          .sort((a, b) => a.emp_name.localeCompare(b.emp_name))
      );
    } catch (error) {
      console.error("Error fetching employees:", error);
      toast.error("Failed to load employees.");
    }
  };

  // Convert datetime
  const convertToDatetimeLocal = (fullDateTime) => {
    if (!fullDateTime) return "";
    return moment(fullDateTime, "YYYY-MM-DD h:mmA").format("YYYY-MM-DDTHH:mm");
  };

  useEffect(() => {
    fetchAttendanceRecords();
    fetchEmployees();
  }, []);

  // Socket.IO
  useEffect(() => {
    const socket = io(API_URL);
    socket.on("attendanceChanged", fetchAttendanceRecords);
    return () => {
      socket.off("attendanceChanged");
      socket.disconnect();
    };
  }, []);

  // Load employee record
  useEffect(() => {
    if (selectedEmployee && !manualSelection) {
      axios
        .get(`${API_URL}/api/attendance?empName=${encodeURIComponent(selectedEmployee)}&t=${Date.now()}`)
        .then((response) => {
          const sorted = response.data.sort((a, b) => new Date(b.date) - new Date(a.date));
          if (sorted.length > 0) {
            const latest = sorted[0];
            setSelectedRecord(latest);
            setApprovedBy(latest.approved_by || "");
            setReason(latest.reason || "");
            setLocation(latest.location || "");
            setClockIn(convertToDatetimeLocal(latest.in_time));
            setClockOut(convertToDatetimeLocal(latest.out_time));
            setUpdateClockIn(false);
            setUpdateClockOut(false);
            setUpdateApprovedBy(false);
            setUpdateReason(false);
            setUpdateLocation(false);
          } else {
            resetForm();
          }
        })
        .catch((error) => {
          console.error("Error fetching attendance:", error);
          toast.error("Error fetching attendance.");
        });
    }
  }, [selectedEmployee, manualSelection]);

  // Handle row click
  const handleRowClick = (record) => {
    setManualSelection(true);
    setSelectedRecord(record);
    setSelectedEmployee(record.emp_name);
    setApprovedBy(record.approved_by || "");
    setReason(record.reason || "");
    setLocation(record.location || "");
    setClockIn(convertToDatetimeLocal(record.in_time));
    setClockOut(convertToDatetimeLocal(record.out_time));
    setUpdateClockIn(false);
    setUpdateClockOut(false);
    setUpdateApprovedBy(false);
    setUpdateReason(false);
    setUpdateLocation(false);
  };

  // Fetch all logs
  const fetchAllLogs = async () => {
    setLoadingLogs(true);
    try {
      const resp = await axios.get(`${API_URL}/api/attendance/logs`);
      const sortedLogs = resp.data.sort((a, b) => new Date(b.changed_at) - new Date(a.changed_at));
      setAttendanceLogs(sortedLogs);
    } catch (err) {
      console.error("Error fetching logs:", err);
      setAttendanceLogs([]);
      toast.error("Failed to load logs.");
    } finally {
      setLoadingLogs(false);
    }
  };

  // Load logs when switching to logs tab
  useEffect(() => {
    if (activeTab === "logs") {
      fetchAllLogs();
    }
  }, [activeTab]);

  // Reset form
  const resetForm = () => {
    setSelectedEmployee("");
    setSelectedRecord(null);
    setApprovedBy("");
    setReason("");
    setLocation("");
    setClockIn("");
    setClockOut("");
    setUpdateClockIn(false);
    setUpdateClockOut(false);
    setUpdateApprovedBy(false);
    setUpdateReason(false);
    setUpdateLocation(false);
    setManualSelection(false);
  };

  // Validate new log form
  const validateNewLogForm = () => {
    const errors = {};
    
    if (!newLog.employee || newLog.employee.trim() === "") {
      errors.employee = "Employee is required";
    }
    
    if (!newLog.date || newLog.date.trim() === "") {
      errors.date = "Date is required";
    }
    
    if (!newLog.clockIn || newLog.clockIn.trim() === "") {
      errors.clockIn = "Clock In time is required";
    }
    
    if (!newLog.clockOut || newLog.clockOut.trim() === "") {
      errors.clockOut = "Clock Out time is required";
    }
    
    if (!newLog.reason || newLog.reason.trim() === "") {
      errors.reason = "Reason is required";
    }
    
    if (!newLog.location || newLog.location.trim() === "") {
      errors.location = "Location is required";
    }

    if (!newLog.approvedBy || newLog.approvedBy.trim() === "") {
      errors.approvedBy = "Approved By is required";
    }

    // Validate that clock out is after clock in
    if (newLog.clockIn && newLog.clockOut) {
      const clockInTime = moment(newLog.clockIn, "HH:mm");
      const clockOutTime = moment(newLog.clockOut, "HH:mm");
      
      if (clockOutTime.isSameOrBefore(clockInTime)) {
        errors.clockOut = "Clock Out must be after Clock In";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Perform update
  const doUpdate = async () => {
    if (!selectedRecord) {
      toast.warn("No record selected.");
      return;
    }
    setShowUpdateConfirm(false);
    const storedUser = JSON.parse(localStorage.getItem("user"));
    const currentUserName = storedUser ? storedUser.name : "";

    const recordDate = moment(selectedRecord.date).format("YYYY-MM-DD");
    const formattedClockIn = updateClockIn && clockIn
      ? `${recordDate} ${moment(clockIn).format("h:mmA")}`
      : selectedRecord.in_time || "";
    const formattedClockOut = updateClockOut && clockOut
      ? `${recordDate} ${moment(clockOut).format("h:mmA")}`
      : selectedRecord.out_time || "";

    const requestBody = {
      inTime: formattedClockIn,
      outTime: formattedClockOut,
      location: updateLocation ? location : selectedRecord.location,
      date: recordDate,
      approved_by: updateApprovedBy ? approvedBy : selectedRecord.approved_by,
      reason: updateReason ? reason : selectedRecord.reason,
      changedBy: currentUserName,
    };

    try {
      await axios.put(`${API_URL}/api/attendance/${selectedRecord.id}`, requestBody);
      toast.success("Attendance updated!");
      resetForm();
      fetchAttendanceRecords();
    } catch (error) {
      console.error("Error updating attendance:", error);
      toast.error("Failed to update.");
    }
  };

  // Add manual attendance record
  const addManualLog = async () => {
    // Validate form
    if (!validateNewLogForm()) {
      toast.error("Please fill all required fields correctly");
      return;
    }

    setSubmittingNewRecord(true);

    const storedUser = JSON.parse(localStorage.getItem("user"));
    const formattedDate = moment(newLog.date).format("YYYY-MM-DD");
    
    // Format times with AM/PM
    const formattedClockIn = moment(newLog.clockIn, "HH:mm").format("h:mmA");
    const formattedClockOut = moment(newLog.clockOut, "HH:mm").format("h:mmA");

    const requestBody = {
      emp_name: newLog.employee,
      date: formattedDate,
      in_time: `${formattedDate} ${formattedClockIn}`,
      out_time: `${formattedDate} ${formattedClockOut}`,
      reason: newLog.reason,
      location: newLog.location,
      approved_by: newLog.approvedBy,
      created_by: storedUser?.name || "System"
    };

    try {
      await axios.post(`${API_URL}/api/attendance/add`, requestBody);
      toast.success("New attendance record added successfully!");
      setShowAddLogModal(false);
      setNewLog({
        employee: "",
        date: "",
        clockIn: "",
        clockOut: "",
        reason: "",
        location: "",
        approvedBy: ""
      });
      setValidationErrors({});
      fetchAttendanceRecords();
    } catch (error) {
      console.error("Error adding attendance record:", error);
      toast.error(error.response?.data?.error || "Failed to add record.");
    } finally {
      setSubmittingNewRecord(false);
    }
  };

  // Handle delete
  const doDelete = async () => {
    if (!selectedRecord) {
      toast.warn("No record selected.");
      return;
    }

    setShowDeleteConfirm(false);

    try {
      await axios.delete(`${API_URL}/api/attendance/${selectedRecord.id}`, {
        data: { changedBy: JSON.parse(localStorage.getItem("user"))?.name || "" }
      });
      toast.success("Record deleted!");
      resetForm();
      fetchAttendanceRecords();
    } catch (err) {
      console.error("Error deleting:", err);
      toast.error("Failed to delete.");
    }
  };

  // Handle log selection
  const handleLogSelect = (logId) => {
    setSelectedLogs(prev =>
      prev.includes(logId)
        ? prev.filter(id => id !== logId)
        : [...prev, logId]
    );
  };

  // Select all logs
  const handleSelectAllLogs = () => {
    if (selectedLogs.length === filteredLogs.length) {
      setSelectedLogs([]);
    } else {
      setSelectedLogs(filteredLogs.map(log => log.log_id));
    }
  };

  // Delete selected logs
  const deleteSelectedLogs = async () => {
    if (selectedLogs.length === 0) {
      toast.warn("Select logs to delete.");
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/attendance/logs`, {
        data: { logIds: selectedLogs }
      });
      toast.success(`${selectedLogs.length} logs deleted!`);
      setSelectedLogs([]);
      fetchAllLogs();
    } catch (error) {
      console.error("Error deleting logs:", error);
      toast.error("Failed to delete logs.");
    }
  };

  // Export logs
  const exportLogsToCSV = () => {
    const logsToExport = filteredLogs;
    const headers = ['Employee', 'Changed By', 'Timestamp', 'Field', 'Old Value', 'New Value'];
    const csvContent = [
      headers.join(','),
      ...logsToExport.map(log => [
        `"${log.emp_name}"`,
        `"${log.changed_by}"`,
        `"${moment(log.changed_at).format('YYYY-MM-DD HH:mm:ss')}"`,
        `"${log.field_name}"`,
        `"${log.old_value || ''}"`,
        `"${log.new_value || ''}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance_logs_${moment().format('YYYY-MM-DD')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Logs exported!");
  };

  // Filter records
  const filteredRecords = attendanceRecords.filter((record) => {
    let approvedMatch = true;
    if (filterApprovedBy) {
      approvedMatch = record.approved_by && record.approved_by.toLowerCase().includes(filterApprovedBy.toLowerCase());
    }
    let employeeMatch = true;
    if (filterEmployee) {
      employeeMatch = record.emp_name && record.emp_name.toLowerCase().includes(filterEmployee.toLowerCase());
    }
    let dateMatch = true;
    if (filterDate) {
      if (filterType === "date") {
        dateMatch = moment(record.date).isSame(moment(filterDate, "YYYY-MM-DD"), "day");
      } else if (filterType === "week") {
        dateMatch = moment(record.date).format("GGGG-[W]WW") === filterDate;
      } else if (filterType === "month") {
        dateMatch = moment(record.date).isSame(moment(filterDate, "YYYY-MM"), "month");
      }
    }
    return approvedMatch && employeeMatch && dateMatch;
  });

  // Filter logs
  const filteredLogs = attendanceLogs.filter((log) => {
    const logDate = moment(log.changed_at);
    let match = true;
    if (logsFilter.dateFrom) match = match && logDate.isSameOrAfter(logsFilter.dateFrom, 'day');
    if (logsFilter.dateTo) match = match && logDate.isSameOrBefore(logsFilter.dateTo, 'day');
    if (logsFilter.changedBy) match = match && log.changed_by.toLowerCase().includes(logsFilter.changedBy.toLowerCase());
    if (logsFilter.employee) match = match && log.emp_name.toLowerCase().includes(logsFilter.employee.toLowerCase());
    if (logsFilter.fieldName) match = match && log.field_name.toLowerCase().includes(logsFilter.fieldName.toLowerCase());
    if (filterMonth) match = match && moment(log.changed_at).format("YYYY-MM") === filterMonth;
    return match;
  });

  // Pagination
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);

  // Get status badge
  const getStatusBadge = (record) => {
    if (!record.in_time) return <Badge bg="secondary">Absent</Badge>;
    if (!record.out_time) return <Badge bg="warning">Missing Out</Badge>;
    return <Badge bg="success">Complete</Badge>;
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilterType("date");
    setFilterDate("");
    setFilterApprovedBy("");
    setFilterEmployee("");
    setCurrentPage(1);
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <Container fluid className="py-3" style={{ backgroundColor: '#f5f7fa' }}>
        <Row className="g-3">
          {/* Left Column - Records/Logs */}
          <Col lg={8}>
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-3">
                <div className="underline-tabs">
                <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="flex-nowrap">
                  {/* Records Tab */}
                  <Tab eventKey="records" title="Attendance Records">
                    {/* Filter Bar */}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div className="d-flex align-items-center gap-2">
                        <Button
                          variant="light"
                          size="sm"
                          onClick={() => setShowFilterBar(!showFilterBar)}
                        >
                          <FaFilter /> {showFilterBar ? 'Hide' : 'Show'} Filters
                        </Button>
                        {filterDate && (
                          <Badge bg="info" className="d-flex align-items-center gap-1">
                            <FaCalendarAlt /> {filterDate}
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 ms-1 text-white"
                              onClick={() => setFilterDate("")}
                            >
                              ×
                            </Button>
                          </Badge>
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <small className="text-muted">{filteredRecords.length} records</small>
                        <Form.Select size="sm" style={{ width: '100px' }} value={recordsPerPage} onChange={(e) => setRecordsPerPage(Number(e.target.value))}>
                          <option value={10}>10/page</option>
                          <option value={20}>20/page</option>
                          <option value={50}>50/page</option>
                        </Form.Select>
                      </div>
                    </div>

                    {showFilterBar && (
                      <div className="bg-light p-3 rounded mb-3">
                        <Row className="g-2">
                          <Col md={3}>
                            <Form.Group>
                              <Form.Label className="small fw-bold">Filter Type</Form.Label>
                              <Form.Select size="sm" value={filterType} onChange={(e) => { setFilterType(e.target.value); setFilterDate(""); }}>
                                <option value="date">Date</option>
                                <option value="week">Week</option>
                                <option value="month">Month</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={3}>
                            <Form.Group>
                              <Form.Label className="small fw-bold">
                                {filterType === "date" ? "Date" : filterType === "week" ? "Week" : "Month"}
                              </Form.Label>
                              <Form.Control
                                size="sm"
                                type={filterType === "date" ? "date" : filterType}
                                value={filterDate}
                                onChange={(e) => setFilterDate(e.target.value)}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={3}>
                            <Form.Group>
                              <Form.Label className="small fw-bold">Employee</Form.Label>
                              <Form.Control
                                size="sm"
                                type="text"
                                placeholder="Search employee"
                                value={filterEmployee}
                                onChange={(e) => setFilterEmployee(e.target.value)}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={3}>
                            <Form.Label className="small fw-bold" style={{ opacity: 0 }}>.</Form.Label>
                            <Button size="sm" variant="outline-secondary" onClick={clearAllFilters} className="w-100">
                              Clear Filters
                            </Button>
                          </Col>
                        </Row>
                      </div>
                    )}

                    {/* Records Table */}
                    <div className="table-responsive" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                      <Table hover size="sm" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                        <thead className="table-light sticky-top">
                          <tr>
                            <th>Employee</th>
                            <th>Date</th>
                            <th>In Time</th>
                            <th>Out Time</th>
                            <th>Work Hours</th>
                            <th>Approved By</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loadingRecords ? (
                            <tr>
                              <td colSpan={6} className="text-center py-4">
                                <div className="spinner-border spinner-border-sm me-2"></div>
                                Loading records...
                              </td>
                            </tr>
                          ) : currentRecords.length > 0 ? (
                            currentRecords.map((record) => (
                              <tr
                                key={record.id}
                                onClick={() => handleRowClick(record)}
                                className={`${selectedRecord?.id === record.id ? 'table-active' : ''}`}
                                style={{ cursor: 'pointer' }}
                              >
                                <td style={{ whiteSpace: 'normal' }}>
                                  <div>{record.emp_name}</div>
                                  <small className="text-muted">{record.location}</small>
                                </td>
                                <td>
                                  <div>{moment(record.date).format("MMM D, YYYY")}</div>
                                </td>
                                <td>
                                  <div className="d-flex align-items-center gap-1">
                                    <FaClock size={12} className="text-primary" />
                                    {record.in_time ? record.in_time.split(' ').pop() : '-'}
                                  </div>
                                </td>
                                <td>
                                  <div className="d-flex align-items-center gap-1">
                                    <FaClock size={12} className="text-success" />
                                    {record.out_time ? record.out_time.split(' ').pop() : '-'}
                                  </div>
                                </td>
                                <td><Badge bg="light" text="dark">{record.work_hour || '0h'}</Badge></td>
                                <td>
                                  {record.approved_by ? (
                                    <Badge bg="info" className="d-flex align-items-center gap-1" style={{ maxWidth: 'fit-content' }}>
                                      <FaUserCheck size={10} />
                                      {record.approved_by}
                                    </Badge>
                                  ) : '-'}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={6} className="text-center py-4 text-muted">
                                <FaExclamationCircle className="me-2" />
                                No records found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>

                    {/* Pagination */}
                    {filteredRecords.length > 0 && (
                      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                        <small className="text-muted">
                          Showing {indexOfFirstRecord + 1} to {Math.min(indexOfLastRecord, filteredRecords.length)} of {filteredRecords.length}
                        </small>
                        <div className="d-flex gap-2 align-items-center">
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                          >
                            Previous
                          </Button>
                          <span className="px-2 py-1 bg-light rounded" style={{ whiteSpace: 'nowrap' }}>
                            Page {currentPage} of {totalPages}
                          </span>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={currentPage === totalPages}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </Tab>

                  {/* Logs Tab */}
                  <Tab eventKey="logs" title={`Change Logs (${attendanceLogs.length})`}>
                    {/* Logs Controls */}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={exportLogsToCSV}
                          disabled={filteredLogs.length === 0}
                        >
                          <FaDownload /> Export
                        </Button>
                        {selectedLogs.length > 0 && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={deleteSelectedLogs}
                          >
                            <FaTrash /> Delete ({selectedLogs.length})
                          </Button>
                        )}
                      </div>
                      <div>
                        <Form.Control
                          size="sm"
                          type="month"
                          value={filterMonth}
                          onChange={(e) => setFilterMonth(e.target.value)}
                          style={{ width: '150px' }}
                          placeholder="Filter by month"
                        />
                      </div>
                    </div>

                    {/* Logs Filters */}
                    <div className="bg-light p-3 rounded mb-3">
                      <Row className="g-2">
                        <Col md={3}>
                          <Form.Control
                            size="sm"
                            placeholder="Employee"
                            value={logsFilter.employee}
                            onChange={(e) => setLogsFilter({...logsFilter, employee: e.target.value})}
                          />
                        </Col>
                        <Col md={3}>
                          <Form.Control
                            size="sm"
                            placeholder="Changed By"
                            value={logsFilter.changedBy}
                            onChange={(e) => setLogsFilter({...logsFilter, changedBy: e.target.value})}
                          />
                        </Col>
                        <Col md={3}>
                          <Form.Control
                            size="sm"
                            placeholder="Field Name"
                            value={logsFilter.fieldName}
                            onChange={(e) => setLogsFilter({...logsFilter, fieldName: e.target.value})}
                          />
                        </Col>
                        <Col md={3}>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => setLogsFilter({ dateFrom: "", dateTo: "", changedBy: "", employee: "", fieldName: "" })}
                            className="w-100"
                          >
                            Clear
                          </Button>
                        </Col>
                      </Row>
                    </div>

                    {/* Logs Table */}
                    <div className="table-responsive" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                      <Table hover size="sm" className="mb-0">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th width="40">
                              <Form.Check
                                type="checkbox"
                                checked={selectedLogs.length === filteredLogs.length && filteredLogs.length > 0}
                                onChange={handleSelectAllLogs}
                              />
                            </th>
                            <th>Employee</th>
                            <th>Changed By</th>
                            <th>Timestamp</th>
                            <th>Field</th>
                            <th>Old Value</th>
                            <th>New Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loadingLogs ? (
                            <tr>
                              <td colSpan={7} className="text-center py-4">
                                <div className="spinner-border spinner-border-sm me-2"></div>
                                Loading logs...
                              </td>
                            </tr>
                          ) : filteredLogs.length > 0 ? (
                            filteredLogs.map((log) => (
                              <tr key={log.log_id}>
                                <td>
                                  <Form.Check
                                    type="checkbox"
                                    checked={selectedLogs.includes(log.log_id)}
                                    onChange={() => handleLogSelect(log.log_id)}
                                  />
                                </td>
                                <td>
                                  <div className="fw-semibold">{log.emp_name}</div>
                                </td>
                                <td>
                                  <Badge bg="secondary">{log.changed_by}</Badge>
                                </td>
                                <td>
                                  <div className="small">
                                    {moment(log.changed_at).format("MMM D, YYYY")}
                                    <br />
                                    <span className="text-muted">{moment(log.changed_at).format("HH:mm:ss")}</span>
                                  </div>
                                </td>
                                <td>
                                  <Badge bg="light" text="dark">{log.field_name}</Badge>
                                </td>
                                <td>
                                  <div className="text-truncate" style={{ maxWidth: '120px' }} title={log.old_value}>
                                    {log.old_value || '-'}
                                  </div>
                                </td>
                                <td>
                                  <div className="text-truncate" style={{ maxWidth: '120px' }} title={log.new_value}>
                                    <Badge bg={log.new_value ? "success" : "secondary"}>
                                      {log.new_value || '-'}
                                    </Badge>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={7} className="text-center py-4 text-muted">
                                <FaExclamationCircle className="me-2" />
                                No logs found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Tab>
                </Tabs>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Right Column - Update Form */}
          <Col lg={4}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="fw-bold mb-0 d-flex align-items-center gap-2">
                    <FaEdit className="text-warning" />
                    Update Attendance
                  </h6>
                  <Button 
                    variant="primary" 
                    size="sm"
                    onClick={() => {
                      setShowAddLogModal(true);
                      setValidationErrors({});
                    }}
                  >
                    <FaPlus /> Add New
                  </Button>
                </div>

                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold">Select Employee</Form.Label>
                    <Form.Select
                      size="sm"
                      value={selectedEmployee}
                      onChange={(e) => {
                        setSelectedEmployee(e.target.value);
                        setManualSelection(false);
                      }}
                    >
                      <option value="">-- Select --</option>
                      {employees.map((emp, idx) => (
                        <option key={idx} value={emp.emp_name}>{emp.emp_name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  {selectedRecord && (
                    <Alert variant="light" className="small mb-3 py-2">
                      <div className="d-flex justify-content-between">
                        <span>Selected:</span>
                        <span className="fw-bold">{selectedRecord.emp_name}</span>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span>Date:</span>
                        <span>{moment(selectedRecord.date).format("MMM D, YYYY")}</span>
                      </div>
                    </Alert>
                  )}

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      label="Update Approved By"
                      checked={updateApprovedBy}
                      onChange={(e) => setUpdateApprovedBy(e.target.checked)}
                      className="mb-2 small"
                    />
                    <Form.Control
                      size="sm"
                      placeholder="Approver name"
                      value={approvedBy}
                      onChange={(e) => setApprovedBy(e.target.value)}
                      disabled={!updateApprovedBy}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      label="Update Reason"
                      checked={updateReason}
                      onChange={(e) => setUpdateReason(e.target.checked)}
                      className="mb-2 small"
                    />
                    <Form.Control
                      size="sm"
                      as="textarea"
                      rows={2}
                      placeholder="Reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      disabled={!updateReason}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      label="Update Location"
                      checked={updateLocation}
                      onChange={(e) => setUpdateLocation(e.target.checked)}
                      className="mb-2 small"
                    />
                    <Form.Control
                      size="sm"
                      placeholder="Location"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      disabled={!updateLocation}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      label="Update Clock In"
                      checked={updateClockIn}
                      onChange={(e) => setUpdateClockIn(e.target.checked)}
                      className="mb-2 small"
                    />
                    <Form.Control
                      size="sm"
                      type="datetime-local"
                      value={clockIn}
                      onChange={(e) => setClockIn(e.target.value)}
                      disabled={!updateClockIn}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      label="Update Clock Out"
                      checked={updateClockOut}
                      onChange={(e) => setUpdateClockOut(e.target.checked)}
                      className="mb-2 small"
                    />
                    <Form.Control
                      size="sm"
                      type="datetime-local"
                      value={clockOut}
                      onChange={(e) => setClockOut(e.target.value)}
                      disabled={!updateClockOut}
                    />
                  </Form.Group>

                  <div className="d-grid gap-2">
                    <Button
                      variant="warning"
                      size="sm"
                      onClick={() => {
                        if (!selectedRecord) {
                          toast.warn("Select a record first.");
                          return;
                        }
                        setShowUpdateConfirm(true);
                      }}
                      disabled={!selectedRecord}
                    >
                      <FaEdit /> Update Record
                    </Button>
                    
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() => setActiveTab("logs")}
                        className="flex-grow-1"
                      >
                        <FaHistory /> View Logs
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => {
                          if (!selectedRecord) {
                            toast.warn("Select a record first.");
                            return;
                          }
                          setShowDeleteConfirm(true);
                        }}
                        disabled={!selectedRecord}
                      >
                        <FaTrash />
                      </Button>
                    </div>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Add New Attendance Record Modal */}
      <Modal 
        show={showAddLogModal} 
        onHide={() => {
          setShowAddLogModal(false);
          setValidationErrors({});
        }} 
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            <FaPlus className="text-primary" />
            Add New Attendance Record
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold">
                <FaUser className="me-1" />
                Employee <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                size="sm"
                value={newLog.employee}
                onChange={(e) => {
                  setNewLog({...newLog, employee: e.target.value});
                  setValidationErrors({...validationErrors, employee: ""});
                }}
                isInvalid={!!validationErrors.employee}
              >
                <option value="">-- Select Employee --</option>
                {employees.map((emp, idx) => (
                  <option key={idx} value={emp.emp_name}>{emp.emp_name}</option>
                ))}
              </Form.Select>
              {validationErrors.employee && (
                <Form.Control.Feedback type="invalid">
                  {validationErrors.employee}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold">
                <FaCalendarAlt className="me-1" />
                Date <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={newLog.date}
                onChange={(e) => {
                  setNewLog({...newLog, date: e.target.value});
                  setValidationErrors({...validationErrors, date: ""});
                }}
                isInvalid={!!validationErrors.date}
              />
              {validationErrors.date && (
                <Form.Control.Feedback type="invalid">
                  {validationErrors.date}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-bold">
                    <FaClock className="me-1 text-primary" />
                    Clock In <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="time"
                    size="sm"
                    value={newLog.clockIn}
                    onChange={(e) => {
                      setNewLog({...newLog, clockIn: e.target.value});
                      setValidationErrors({...validationErrors, clockIn: ""});
                    }}
                    isInvalid={!!validationErrors.clockIn}
                  />
                  {validationErrors.clockIn && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.clockIn}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-bold">
                    <FaClock className="me-1 text-success" />
                    Clock Out <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="time"
                    size="sm"
                    value={newLog.clockOut}
                    onChange={(e) => {
                      setNewLog({...newLog, clockOut: e.target.value});
                      setValidationErrors({...validationErrors, clockOut: ""});
                    }}
                    isInvalid={!!validationErrors.clockOut}
                  />
                  {validationErrors.clockOut && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.clockOut}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold">
                Reason <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                size="sm"
                placeholder="Enter reason for attendance"
                value={newLog.reason}
                onChange={(e) => {
                  setNewLog({...newLog, reason: e.target.value});
                  setValidationErrors({...validationErrors, reason: ""});
                }}
                isInvalid={!!validationErrors.reason}
              />
              {validationErrors.reason && (
                <Form.Control.Feedback type="invalid">
                  {validationErrors.reason}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold">
                <FaMapMarkerAlt className="me-1" />
                Location <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                size="sm"
                placeholder="Enter work location"
                value={newLog.location}
                onChange={(e) => {
                  setNewLog({...newLog, location: e.target.value});
                  setValidationErrors({...validationErrors, location: ""});
                }}
                isInvalid={!!validationErrors.location}
              />
              {validationErrors.location && (
                <Form.Control.Feedback type="invalid">
                  {validationErrors.location}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold">
                <FaUserCheck className="me-1" />
                Approved By <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                size="sm"
                placeholder="Name of approver"
                value={newLog.approvedBy}
                onChange={(e) => {
                  setNewLog({...newLog, approvedBy: e.target.value});
                  setValidationErrors({...validationErrors, approvedBy: ""});
                }}
                isInvalid={!!validationErrors.approvedBy}
              />
              {validationErrors.approvedBy && (
                <Form.Control.Feedback type="invalid">
                  {validationErrors.approvedBy}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            {Object.keys(validationErrors).length > 0 && (
              <Alert variant="danger" className="py-2 small">
                <FaExclamationCircle className="me-1" />
                Please fill all required fields correctly
              </Alert>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            size="sm" 
            onClick={() => {
              setShowAddLogModal(false);
              setValidationErrors({});
            }}
            disabled={submittingNewRecord}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            size="sm" 
            onClick={addManualLog}
            disabled={submittingNewRecord}
          >
            {submittingNewRecord ? (
              <>
                <span className="spinner-border spinner-border-sm me-1"></span>
                Adding...
              </>
            ) : (
              <>
                <FaPlus /> Add Record
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Update Confirmation Modal */}
      <Modal show={showUpdateConfirm} onHide={() => setShowUpdateConfirm(false)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">Confirm Update</Modal.Title>
        </Modal.Header>
        <Modal.Body className="small">
          <p>Update attendance for <strong>{selectedRecord?.emp_name}</strong>?</p>
          <div className="bg-light p-2 rounded">
            <div className="d-flex justify-content-between">
              <span>Date:</span>
              <span>{moment(selectedRecord?.date).format("MMM D, YYYY")}</span>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowUpdateConfirm(false)}>
            Cancel
          </Button>
          <Button variant="warning" size="sm" onClick={doUpdate}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered size="sm">
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title className="fs-6">Delete Record</Modal.Title>
        </Modal.Header>
        <Modal.Body className="small">
          <div className="text-center">
            <FaExclamationCircle className="text-danger mb-2" size={24} />
            <p>Delete record for <strong>{selectedRecord?.emp_name}</strong> on <strong>{moment(selectedRecord?.date).format("MMM D, YYYY")}</strong>?</p>
            <p className="text-danger small">This action cannot be undone.</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={doDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UpdateAttendance;